import type {
	CAButtonStyle,
	CAButtonType,
	CAModalAlignment,
	CAModalSize,
	CAModalStyle,
} from '@ds/interfaces'
import { defineComponent, type PropType } from 'vue'

import CAButtonGroupFooter from '../buttons/CAButtonGroupFooter'
import CACloseButton from '../buttons/CACloseButton'
import CAHeading from '../headers/CAHeading'
import CABackdrop from './CABackdrop'

/**
 * The Modal component is a modal that pops up over the content of the page. It can be used to display information, forms, or other content.
 *
 * @component
 *
 * Types needed for the Modal component
 * @typedef {('full' | 'large' | 'medium' | 'small' | 'x-large')} ModalSize - The size of the modal
 *
 * @prop {String} additionalClasses - Use additional classes to apply to the modal
 * @prop {CAModalAlignment} alignment - The alignment of the modal content: 'centered' | 'left-aligned'
 * @prop {String} [backDataTest='back-button'] - The data-test attribute for the back button in the footer.
 * @prop {String} [backText='Go back'] - The text for the back button in the footer.
 * @prop {Boolean} [borderedTitle=false] - Whether the title should have a border.
 * @prop {String} [buttonsDirection='row'] - The direction of the buttons in the footer: 'row' | 'column'
 * @prop {String} [cancelDataTest='modal-close-button'] - The data-test attribute for the cancel button in the footer.
 * @prop {String} [cancelText='Cancel'] - The text for the cancel button in the footer.
 * @prop {Boolean} [closeButton=true] - Whether to show the close button in the modal.
 * @prop {String} [dataTest=''] - The data-test attribute for the modal.
 * @prop {Boolean} [disableTitleMutation=true] - Whether the title capitalization should be overwritten.
 * @prop {Boolean} [disabled=false] - Whether the submit button is disabled.
 * @prop {Boolean} [disabledCancel=false] - Whether the cancel button is disabled.
 * @prop {Boolean} [enableFooter=true] - Whether the modal should have a footer. By default, the footer will contain a submit and cancel button.
 * @prop {String} [iconBeforeTitle=''] - Whether the title has an icon before it.
 * @prop {Boolean} isOpen - Whether or not the modal is open.
 * @prop {CAModalSize} modalSize - The size of modal: 'full' | 'large' | 'medium' | 'small' | 'x-large'
 * @prop {CAModalStyle} modalStyle - The type of modal: 'danger' | 'default' | 'gray' | 'success'
 * @prop {String} [nextDataTest='modal-next-button'] - The data-test attribute for the next button in the footer.
 * @prop {String} [nextText='Next'] - The text for the next button in the footer.
 * @prop {Boolean} [showBackButton=false] - Whether to show the back button in the footer.
 * @prop {Boolean} [showCancelButton=true] - Whether to show the cancel button in the footer.
 * @prop {Boolean} [showNextButton=false] - Whether to show the next button in the footer.
 * @prop {Boolean} [showSubmitButton=true] - Whether to show the submit button in the footer.
 * @prop {String} [submitDataTest='modal-action-button'] - The data-test attribute for the submit button in the footer.
 * @prop {CAButtonStyle} [submitStyle='filled-dark'] - The style of the submit button in the footer: 'filled-dark' | 'filled-light' | 'outlined-dark' | 'outlined-light'
 * @prop {String} [submitText='Submit'] - The text for the submit button in the footer.
 * @prop {String} [title=''] - The title of the modal.
 *
 * @slot default - The default slot for the modal content
 * @slot footer - The footer slot for the modal. `enableFooter` must be used in order to use this slot. Using this template will override the footer submit/cancel buttons.
 * @slot title - The title slot for custom title content
 *
 * @emit back - Emits when the back button from footer is clicked
 * @emit cancel - Emits when the cancel button from footer is clicked
 * @emit next - Emits when the next button from footer is clicked
 * @emit opened - Emits when the modal is opened
 * @emit submit - Emits when the submit button from footer is clicked
 */
export default defineComponent({
	name: 'CAModal',

	props: {
		/**
		 * Use additional classes to apply to the modal
		 *
		 * @default ''
		 * @type {String}
		 */
		additionalClasses: { default: '', type: String },
		/**
		 * The alignment of the modal content: 'centered' | 'left-aligned'
		 *
		 * @default 'centered'
		 * @type {ModalAlignment}
		 */
		alignment: { default: 'centered', type: String as PropType<CAModalAlignment> },
		/**
		 * The data-test attribute for the back button in the footer.
		 *
		 * @default 'back-button'
		 * @type {String}
		 */
		backDataTest: { default: 'back-button', type: String },
		/**
		 * The text for the back button in the footer.
		 *
		 * @default 'Go back'
		 * @type {String}
		 */
		backText: { default: 'Go back', type: String },
		/**
		 * Whether the title should have a border.
		 *
		 * @default false
		 * @type {Boolean}
		 */
		borderedTitle: { default: false, type: Boolean },
		/**
		 * The direction of the buttons in the footer: 'row' | 'column'
		 *
		 * @default 'row'
		 * @type {String}
		 * @enum {'row' | 'column'}
		 */
		buttonsDirection: { default: 'row', type: String as PropType<'column' | 'row'> },
		/**
		 * The justification of the buttons in the footer: 'end' | 'space-between'
		 *
		 * @default 'space-between'
		 * @type {String}
		 * @enum {'end' | 'space-between'}
		 */
		buttonsJustify: { default: 'space-between', type: String as PropType<'end' | 'space-between'> },
		/**
		 * The data-test attribute for the cancel button in the footer.
		 *
		 * @default 'modal-close-button'
		 * @type {String}
		 */
		cancelDataTest: { default: 'modal-close-button', type: String },
		/**
		 * The text for the cancel button in the footer.
		 *
		 * @default 'Cancel'
		 * @type {String}
		 */
		cancelText: { default: 'Cancel', type: String },
		/**
		 * Whether to show the close button in the modal.
		 *
		 * @default true
		 * @type {Boolean}
		 */
		closeButton: { default: true, type: Boolean },
		/**
		 * The data-test attribute for the modal.
		 *
		 * @default ''
		 * @type {String}
		 */
		dataTest: { default: '', type: String },
		/**
		 * Whether the title capitalization should be overwritten.
		 *
		 * @default true
		 * @type {Boolean}
		 */
		disableTitleMutation: { default: true, type: Boolean },
		/**
		 * Whether the submit button is disabled.
		 *
		 * @default false
		 * @type {Boolean}
		 */
		disabled: { default: false, type: Boolean },
		/**
		 * Whether the cancel button is disabled.
		 *
		 * @default false
		 * @type {Boolean}
		 */
		disabledCancel: { default: false, type: Boolean },
		/**
		 * Whether the modal should have a footer. By default, the footer will contain a submit and cancel button.
		 *
		 * @default true
		 * @type {Boolean}
		 */
		enableFooter: { default: true, type: Boolean },
		/**
		 * Whether the title has an icon before it.
		 *
		 * @default '''
		 * @type {String}
		 */
		iconBeforeTitle: { default: '', type: String },
		/**
		 * Whether or not the modal is open.
		 *
		 * @default false
		 * @type {Boolean}
		 */
		isOpen: { required: true, type: Boolean },
		/**
		 * The size of modal: 'full' | 'large' | 'medium' | 'small' | 'x-large'
		 *
		 * @default 'medium'
		 * @type {ModalSize}
		 */
		modalSize: { default: 'medium', type: String as PropType<CAModalSize> },
		/**
		 * The type of modal: 'danger' | 'default' | 'gray' | 'success'
		 *
		 * @default 'default'
		 * @type {ModalStyle}
		 */
		modalStyle: { default: 'default', type: String as PropType<CAModalStyle> },
		/**
		 * The data-test attribute for the next button in the footer.
		 *
		 * @default 'modal-next-button'
		 * @type {String}
		 */
		nextDataTest: { default: 'modal-next-button', type: String },
		/**
		 * The text for the next button in the footer.
		 *
		 * @default 'Next'
		 * @type {String}
		 */
		nextText: { default: 'Next', type: String },
		/**
		 * Whether to show the back button in the footer.
		 *
		 * @default false
		 * @type {Boolean}
		 */
		showBackButton: { default: false, type: Boolean },
		/**
		 * Whether to show the cancel button in the footer.
		 *
		 * @default true
		 * @type {Boolean}
		 */
		showCancelButton: { default: true, type: Boolean },
		/**
		 * Whether to show the next button in the footer.
		 *
		 * @default false
		 * @type {Boolean}
		 */
		showNextButton: { default: false, type: Boolean },
		/**
		 * Whether to show the submit button in the footer.
		 *
		 * @default true
		 * @type {Boolean}
		 */
		showSubmitButton: { default: true, type: Boolean },
		/**
		 * The data-test attribute for the submit button in the footer.
		 *
		 * @default 'modal-action-button'
		 * @type {String}
		 */
		submitDataTest: { default: 'modal-action-button', type: String },
		/**
		 * The style of the submit button in the footer: 'filled-dark' | 'filled-light' | 'outlined-dark' | 'outlined-light'
		 *
		 * @default 'filled-dark'
		 * @type {ButtonStyle}
		 */
		submitStyle: { default: 'filled-dark', type: String as PropType<CAButtonStyle> },
		/**
		 * The text for the submit button in the footer.
		 *
		 * @default 'Submit'
		 * @type {String}
		 */
		submitText: { default: 'Submit', type: String },
		/**
		 * The title of the modal.
		 *
		 * @default ''
		 * @type {String}
		 */
		title: { default: '', type: String },
	},

	emits: {
		back: () => true,
		cancel: () => true,
		next: () => true,
		opened: () => true,
		submit: () => true,
	},

	computed: {
		computedButtonType(): CAButtonType {
			switch (this.modalStyle) {
				case 'danger':
					return 'danger'

				case 'gray':
					return 'secondary'

				case 'success':
					return 'success'

				default:
					return 'primary'
			}
		},
	},

	watch: {
		isOpen: {
			handler() {
				if (this.isOpen) {
					try {
						this.$emit('opened')
					} catch (error) {
						console.error('Error emitting opened event:', error)
					}
				}
			},
		},
	},

	methods: {
		closeModal() {
			try {
				this.$emit('cancel')
			} catch (error) {
				console.error('Error emitting cancel event:', error)
			}
		},

		handleBackClick() {
			try {
				this.$emit('back')
			} catch (error) {
				console.error('Error emitting back event:', error)
			}
		},

		handleCancelClick() {
			try {
				this.$emit('cancel')
			} catch (error) {
				console.error('Error emitting cancel event:', error)
			}
		},

		handleNextClick() {
			try {
				this.$emit('next')
			} catch (error) {
				console.error('Error emitting next event:', error)
			}
		},

		handleSubmitClick() {
			try {
				this.$emit('submit')
			} catch (error) {
				console.error('Error emitting submit event:', error)
			}
		},
	},

	render() {
		return (
			<div>
				{this.isOpen &&
					<div
						aria-labelledby={this.dataTest}
						aria-modal="true"
						class={[
							'ca-modal',
							this.additionalClasses,
						]}
						data-test={this.dataTest}
						data-test-component="ca-modal"
						role="dialog"
					>
						{this.closeButton ?
							<CABackdrop
								data-test="ca-backdrop"
								open={this.isOpen}
								onClick={this.closeModal}
							/>

							:

							<CABackdrop open={this.isOpen} />
						}

						<div class="ca-modal__container">
							<div class="ca-modal__content">
								<div
									class={[
										'ca-modal__box',
										`ca-modal__box-${this.alignment}`,
										`ca-modal__${this.buttonsDirection}`,
										`ca-modal__box--${this.modalSize}`,
									]}
									data-test="ca-modal-box"
								>
									{this.closeButton &&
										<CACloseButton
											class="ca-modal__center-aligned-button"
											onClose={this.handleCancelClick}
										/>
									}

									<div
										class={{
											'ca-modal__wrapper': true,
											'ca-modal__wrapper-has-footer': this.enableFooter,
										}}
									>
										<div
											class={{
												'ca-modal__wrapper--title': true,
												'ca-modal__wrapper--title__has-border': this.borderedTitle,
											}}
										>
											{this.title &&
												<CAHeading
													disableTitleMutation={this.disableTitleMutation}
													heading={4}
													iconBeforeTitle={this.iconBeforeTitle}
													title={this.title}
												/>
											}
											{this.$slots.title?.()}
										</div>

										<div class="ca-modal__box__content" data-test="ca-modal-box-content">
											{this.$slots.default?.()}
										</div>

										{this.enableFooter &&
											<div class="ca-modal__footer" data-test="ca-modal-footer">
												{!this.$slots.footer?.() ?
													<CAButtonGroupFooter
														backDataTest={this.backDataTest}
														backText={this.backText}
														buttonsDirection={this.buttonsDirection}
														buttonType={this.computedButtonType}
														cancelDataTest={this.cancelDataTest}
														cancelText={this.cancelText}
														class={`ca-button-group-footer--${this.buttonsJustify}`}
														disabled={this.disabled}
														disabledCancel={this.disabledCancel}
														nextDataTest={this.nextDataTest}
														nextText={this.nextText}
														showBackButton={this.showBackButton}
														showCancelButton={this.showCancelButton}
														showNextButton={this.showNextButton}
														showSubmitButton={this.showSubmitButton}
														submitDataTest={this.submitDataTest}
														submitStyle={this.submitStyle}
														submitText={this.submitText}
														onBack={this.handleBackClick}
														onCancel={this.handleCancelClick}
														onNext={this.handleNextClick}
														onSubmit={this.handleSubmitClick}
													/>

													:

													this.$slots.footer?.()
												}
											</div>
										}
									</div>
								</div>
							</div>
						</div>
					</div>
				}
			</div>
		)
	},
})
